import React, {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import { PrismicStoreLocation } from 'src/typings/generated/graphql'

import Radar from 'radar-sdk-js'
import type RadarMap from 'radar-sdk-js/dist/ui/RadarMap'
import type RadarMarker from 'radar-sdk-js/dist/ui/RadarMarker'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'radar-sdk-js/dist/radar.css'
import { filterVenuesByBounds } from 'src/constants/worldCupVenues'
import slugify from 'slugify'

export interface IVisibleLocation {
  locationIndex: number
  location: PrismicStoreLocation
  distanceFromCurrentLocation: number | null
}

type LatLng = { lat: number; lng: number }

interface StoreLocation {
  url: string
  lng: number
  lat: number
}

type Props = {
  mapCenter: LatLng
  locations: PrismicStoreLocation[]
  setVisibleLocations?: Dispatch<SetStateAction<IVisibleLocation[]>>
}

const VENUE_MARKER_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="68" viewBox="0 0 44 54">' +
  '<defs><filter id="vs" x="-20%" y="-10%" width="140%" height="140%"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.35"/></filter></defs>' +
  '<g filter="url(#vs)">' +
  '<path d="M22 48 C22 48 6 32 6 20 A16 16 0 1 1 38 20 C38 32 22 48 22 48Z" fill="#002664" stroke="#ffffff" stroke-width="2"/>' +
  '<ellipse cx="22" cy="17" rx="10" ry="3.5" fill="none" stroke="#ffffff" stroke-width="1.5"/>' +
  '<path d="M12 17 L13.5 24 L30.5 24 L32 17" fill="none" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"/>' +
  '<line x1="15" y1="24" x2="15" y2="28" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"/>' +
  '<line x1="22" y1="24" x2="22" y2="28" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"/>' +
  '<line x1="29" y1="24" x2="29" y2="28" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"/>' +
  '<line x1="13" y1="28" x2="31" y2="28" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"/>' +
  '</g></svg>'

const createStoreMarkerElement = (url: string): HTMLDivElement => {
  const el = document.createElement('div')
  const hasUrl = url.length > 0
  const baseStyles =
    'width:14px;height:14px;background:#d81c2f;border:2px solid white;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,0.3);'
  el.style.cssText = hasUrl
    ? `${baseStyles}cursor:pointer;pointer-events:auto;`
    : baseStyles
  if (hasUrl) {
    el.addEventListener('click', e => {
      e.stopPropagation()
      window.open(url, '_blank', 'noopener,noreferrer')
    })
  }
  return el
}

const createVenueMarkerElement = (
  name: string,
  city: string
): HTMLDivElement => {
  const el = document.createElement('div')
  el.title = `${name} — ${city}`
  el.style.cssText = 'cursor:pointer;'
  el.innerHTML = VENUE_MARKER_SVG
  return el
}

const getStoreExternalUrl = (location: PrismicStoreLocation): string => {
  const ext = location.data.external_location_data
  if (!ext?.state || !ext?.city || !ext?.address1) return ''
  return `https://locations.raisingcanes.com/${ext.state.toLowerCase()}/${slugify(
    ext.city,
    { lower: true }
  )}/${slugify(ext.address1, { remove: /['#,]/g, lower: true })}`
}

const INITIAL_ZOOM = 10

const WorldCupLocationsMap = forwardRef<undefined, Props>(
  ({ locations, setVisibleLocations, mapCenter }) => {
    const mapRef = useRef<RadarMap | null>(null)
    const markersRef = useRef<RadarMarker[]>([])

    // Build store locations with external URLs
    const storeLocations: StoreLocation[] = useMemo(
      () =>
        (locations ?? []).reduce<StoreLocation[]>((acc, location) => {
          const lng = location.data.coordinates?.longitude ?? 0
          const lat = location.data.coordinates?.latitude ?? 0
          if (lng === 0 && lat === 0) return acc
          acc.push({ url: getStoreExternalUrl(location), lng, lat })
          return acc
        }, []),
      [locations]
    )

    // Render markers based on current map bounds
    const renderMarkers = useCallback(() => {
      const map = mapRef.current
      if (!map) return

      // Clear existing markers
      markersRef.current.forEach(m => m.remove())
      markersRef.current = []

      // Filter to only stores within the visible map bounds
      const bounds = map.getBounds()
      const west = bounds.getWest()
      const east = bounds.getEast()
      const south = bounds.getSouth()
      const north = bounds.getNorth()

      const visibleStores = storeLocations.filter(
        s => s.lng >= west && s.lng <= east && s.lat >= south && s.lat <= north
      )

      // Add individual store markers
      visibleStores.forEach(store => {
        const markerEl = createStoreMarkerElement(store.url)
        const marker = Radar.ui
          .marker({ element: markerEl })
          .setLngLat([store.lng, store.lat])
          .addTo(map)
        markersRef.current.push(marker)
      })

      // Update visible locations count
      if (setVisibleLocations && typeof setVisibleLocations === 'function') {
        const visibleList: IVisibleLocation[] = Array.from(
          { length: visibleStores.length },
          (_, idx) => ({
            locationIndex: idx,
            location: locations[0],
            distanceFromCurrentLocation: null,
          })
        )
        setVisibleLocations(visibleList)
      }

      // Add World Cup venue markers
      const venues = filterVenuesByBounds([west, south, east, north])
      venues.forEach(venue => {
        const markerEl = createVenueMarkerElement(venue.name, venue.city)
        const marker = Radar.ui
          .marker({ element: markerEl })
          .setLngLat([venue.longitude, venue.latitude])
          .addTo(map)
        markersRef.current.push(marker)
      })
    }, [storeLocations])

    // Initialize map once on mount
    useEffect(() => {
      if (!process.env.GATSBY_RADAR_API_KEY) return

      Radar.initialize(`${process.env.GATSBY_RADAR_API_KEY}`)

      const map = Radar.ui.map({
        container: 'world-cup-map',
        style: 'radar-default-v1',
        center: [mapCenter.lng, mapCenter.lat],
        zoom: INITIAL_ZOOM,
        boxZoom: true,
        interactive: true,
        scrollZoom: true,
      })

      mapRef.current = map

      // Render markers after map loads
      map.on('load', renderMarkers)

      // Re-render markers after flyTo completes
      map.on('moveend', renderMarkers)

      // eslint-disable-next-line consistent-return
      return () => {
        map.off('load', renderMarkers)
        map.off('moveend', renderMarkers)
        map.remove()
      }
    }, [])

    // Pan to new venue when selection changes
    useEffect(() => {
      const map = mapRef.current
      if (!map) return

      map.flyTo({
        center: [mapCenter.lng, mapCenter.lat],
        zoom: INITIAL_ZOOM,
      })
      // markers will re-render via the 'moveend' listener
    }, [mapCenter.lat, mapCenter.lng])

    return (
      <div
        id="world-cup-map"
        style={{ height: '100%', position: 'absolute', width: '100%' }}
      />
    )
  }
)

export default WorldCupLocationsMap
